import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { CornerDownRight, Loader2, Send, X, SmilePlus } from "lucide-react";
import api, { apiErr, fileUrl } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const REACTIONS = ["🙏", "❤️", "😂"];
const AV_COLORS = ["#2563eb", "#E0A73A", "#1E8A5B", "#0E7C86", "#C2557A", "#7C4DB8"];
const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const colorFor = (id) => AV_COLORS[(id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AV_COLORS.length];

function Avatar({ c, size = 34 }) {
  if (c.author_photo) {
    return <img src={fileUrl(`/api/files/${c.author_photo}`, 200)} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, background: colorFor(c.author_id) }}>
      {initials(c.author_name)}
    </div>
  );
}

function Comment({ c, isReply, canModerate, onReact, onReply, onDelete }) {
  const [pick, setPick] = useState(false);
  const when = c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : "";
  const counts = c.reaction_counts || {};
  const mine = c.my_reactions || [];
  const summaryEmojis = REACTIONS.filter((e) => counts[e] > 0);
  const total = summaryEmojis.reduce((a, e) => a + counts[e], 0);
  return (
    <div className={`flex gap-2.5 ${isReply ? "mt-2.5" : ""}`} data-testid={isReply ? "comment-reply-item" : "comment-item"}>
      <Avatar c={c} size={isReply ? 28 : 34} />
      <div className="flex-1 min-w-0">
        <div className="inline-block max-w-full rounded-2xl px-3.5 py-2 relative" style={{ background: "rgba(13,35,64,0.05)" }}>
          <div className="font-semibold text-[13px] text-[var(--brand-navy)] leading-tight">{c.author_name}</div>
          <div className="text-[14px] text-[#2C2C3E]/90 whitespace-pre-wrap break-words leading-snug mt-0.5">{c.body}</div>
          {total > 0 && (
            <div className="absolute -bottom-2 right-1 flex items-center gap-0.5 rounded-full bg-white shadow-sm border px-1.5 py-0.5 text-[11px] font-semibold" data-testid={`comment-reactions-${c.id}`}>
              <span>{summaryEmojis.join("")}</span><span className="text-muted-foreground">{total}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3.5 mt-1.5 pl-1 text-[12px] font-semibold text-muted-foreground relative">
          <span className="font-normal">{when}</span>
          {pick ? (
            <div className="flex items-center gap-1.5" data-testid={`comment-react-bar-${c.id}`}>
              {REACTIONS.map((e) => (
                <button key={e} data-testid={`comment-react-${c.id}-${e}`}
                  onClick={() => { onReact(c, e); setPick(false); }}
                  className={`text-base leading-none rounded-full px-1 transition-transform hover:scale-125 ${mine.includes(e) ? "scale-110" : ""}`}
                  style={mine.includes(e) ? { filter: "none" } : {}}>{e}</button>
              ))}
              <button onClick={() => setPick(false)} className="hover:text-destructive" aria-label="Close reactions"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <button data-testid={`comment-react-btn-${c.id}`} onClick={() => setPick(true)}
              className={`flex items-center gap-1 transition-colors ${mine.length ? "text-[var(--brand-navy)]" : "hover:text-[var(--brand-navy)]"}`}>
              {mine.length ? <span className="text-sm">{mine.join("")}</span> : <><SmilePlus className="h-3.5 w-3.5" /> React</>}
            </button>
          )}
          <button data-testid={`comment-reply-${c.id}`} onClick={() => onReply(c)} className="hover:text-[var(--brand-navy)]">Reply</button>
          {canModerate(c) && (
            <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c)} className="hover:text-destructive">Delete</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommentThread({ basePath, userId, isAdmin, comments, setComments, onCountChange, placeholder = "Add a comment…" }) {
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const canModerate = (c) => isAdmin || c.author_id === userId;

  const tops = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);

  const add = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const parent_id = replyTo ? (replyTo.parent_id || replyTo.id) : null;
      const { data } = await api.post(`${basePath}/comments`, { body: text.trim(), parent_id });
      setComments([...comments, data]);
      onCountChange?.(comments.length + 1);
      setText(""); setReplyTo(null);
    } catch (e) { import("sonner").then(({ toast }) => toast.error(apiErr(e))); }
    finally { setSending(false); }
  };

  const react = async (c, emoji) => {
    const snapshot = { reaction_counts: c.reaction_counts, my_reactions: c.my_reactions };
    setComments((prev) => prev.map((x) => {
      if (x.id !== c.id) return x;
      const mine = new Set(x.my_reactions || []);
      const counts = { ...(x.reaction_counts || {}) };
      if (mine.has(emoji)) { mine.delete(emoji); counts[emoji] = (counts[emoji] || 1) - 1; if (counts[emoji] <= 0) delete counts[emoji]; }
      else { mine.add(emoji); counts[emoji] = (counts[emoji] || 0) + 1; }
      return { ...x, my_reactions: [...mine], reaction_counts: counts };
    }));
    try {
      const { data } = await api.post(`/comments/${c.id}/react`, { emoji });
      setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, reaction_counts: data.reaction_counts, my_reactions: data.my_reactions } : x));
    } catch (e) {
      setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, ...snapshot } : x));
      import("sonner").then(({ toast }) => toast.error(apiErr(e)));
    }
  };

  const del = async (c) => {
    try {
      await api.delete(`/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id && x.parent_id !== c.id));
      onCountChange?.(Math.max(0, comments.length - 1));
    } catch (e) { import("sonner").then(({ toast }) => toast.error(apiErr(e))); }
  };

  return (
    <div className="space-y-4">
      {tops.map((c) => (
        <div key={c.id}>
          <Comment c={c} canModerate={canModerate} onReact={react} onReply={setReplyTo} onDelete={del} />
          {repliesOf(c.id).length > 0 && (
            <div className="ml-6 pl-3 border-l-2" style={{ borderColor: "rgba(13,35,64,0.08)" }}>
              {repliesOf(c.id).map((r) => (
                <Comment key={r.id} c={r} isReply canModerate={canModerate} onReact={react} onReply={setReplyTo} onDelete={del} />
              ))}
            </div>
          )}
        </div>
      ))}

      {comments.length === 0 && !replyTo && (
        <div className="text-[13px] text-muted-foreground pl-1" data-testid="comment-first-nudge">
          Be the first to encourage them <span aria-hidden>💛</span>
        </div>
      )}
      {replyTo && (
        <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground pl-1" data-testid="comment-replying-to">
          <CornerDownRight className="h-3.5 w-3.5" /> Replying to <span className="font-semibold text-[var(--brand-navy)]">{replyTo.author_name}</span>
          <button onClick={() => setReplyTo(null)} className="ml-1 hover:text-destructive" aria-label="Cancel reply"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder={replyTo ? `Reply to ${replyTo.author_name}…` : placeholder} className="rounded-full h-10" />
        <Button data-testid="comment-send-btn" onClick={add} disabled={sending} size="icon" className="rounded-full accent-bg text-white shrink-0">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
